import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemContent, Link, Divider } from '@mui/joy';
import { animate, stagger } from 'motion';
import { useEffect, useRef } from 'react';

const TermsOfService = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.animate-in');
        
        animate(
            elements,
            { 
                opacity: [0, 1],
                y: [20, 0]
            },
            { 
                delay: stagger(0.1),
                duration: 0.6,
                easing: 'ease-out'
            }
        );
    }, []);

    return (
        <Box ref={containerRef}>
            <Container sx={{ py: 8 }}>
                <Box className="animate-in" sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        level="h1"
                        fontSize="xl4"
                        sx={{
                            mb: 3,
                            bgcolor: 'text.primary',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Terms of Service
                    </Typography>
                    <Typography 
                        level="body1" 
                        fontSize="lg" 
                        sx={{ 
                            maxWidth: 800, 
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.6
                        }}
                    >
                        From Hubitable, thank you for using our products! We build them to help you do your best work. There are millions of people using Hubitable products every day. Because we don't know every one of our customers personally, we have to put in place some Terms of Service to help keep the ship afloat.
                    </Typography>
                </Box>

                <Box className="animate-in" sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        When we say "Company", "we", "our", or "us" in this document, we are referring to{' '}
                        <Link href="https://hubitable.com" sx={{ color: 'primary.500' }}>
                            Hubitable Ltd
                        </Link>.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        When we say "Services", we mean our websites, including hubitable.com, and any product created and maintained by Hubitable Ltd. That includes Hubitable Gym Owner, Hubitable Athlete, and Hubitable Chef, whether delivered within a web browser, desktop application, mobile application, or another format.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        When we say "You" or "your", we are referring to the people or organizations that own an account with one or more of our Services mentioned above.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        We may update these Terms of Service ("Terms") in the future. You can track all changes made since early-2025{' '}
                        <Link href="https://hubitable.com/terms" sx={{ color: 'primary.500' }}>
                            on our website
                        </Link>. Whenever we make a significant change to our policies, we will update the date at the top of this page and take any other appropriate steps to notify account holders.
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        When you use our Services, now or in the future, you are agreeing to the latest Terms. There may be times where we do not exercise or enforce a right or provision of the Terms; however, that does not mean we are waiving that right or provision. <strong>These Terms do contain a limitation of our liability.</strong>
                    </Typography>

                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        If you violate any of the Terms, we may terminate your account. We do our best to deserve your trust by being open about{' '}
                        <Link href="https://hubitable.com/about" sx={{ color: 'primary.500' }}>
                            who we are
                        </Link>,{' '}
                        <Link href="https://hubitable.com/about/our-work" sx={{ color: 'primary.500' }}>
                            how we work
                        </Link>, and keeping an open door to{' '}
                        <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                            your feedback
                        </Link>.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Account Terms
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You are responsible for maintaining the security of your account and password and for ensuring that any of your users do the same. <strong>We are committed to providing reasonable security measures to protect your account, as required by UK law, but we cannot be liable for loss or damage from your failure to comply with this security obligation.</strong> We recommend all users set up two-factor authentication for added security. In some of our Services, we may require it.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You may not use the Services for any purpose outlined in our{' '}
                                    <Link href="/terms/use_restrictions" sx={{ color: 'primary.500' }}>
                                        Use Restrictions policy
                                    </Link>, which is designed to be fair and transparent as required by UK law, and you may not permit any of your users to do so, either.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You are responsible for all content posted to and activity that occurs under your account, including content posted by and activity of any users in your account. <strong>For UK users, this includes compliance with applicable content laws.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You must be a human. Accounts registered by "bots" or other automated methods are not permitted and appropriate action will be taken.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Payment, Refunds, and Plan Changes
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    If you are using a free version of one of our Services, it is really free: we do not ask you for your credit card and — just like for customers who pay for our Services — we do not sell your data.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    For paid Services that offer a free trial, we explain the length of trial when you sign up. <strong>For UK users, you may cancel during the trial period with no charge, as per your statutory rights.</strong> After the trial period, you need to pay in advance to keep using the Service. If you do not pay, we will freeze your account and it will be inaccessible until you make payment. If your account has been frozen for a while, we will queue it up for auto-cancellation. See our{' '}
                                    <Link href="/terms/cancellation" sx={{ color: 'primary.500' }}>
                                        Cancellation policy
                                    </Link> for more details.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    If you are upgrading from a free plan to a paid plan, we will charge your card immediately and your billing cycle starts on the day of upgrade. For other upgrades or downgrades in plan level, the new rate starts from the next billing cycle.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    All fees are exclusive of all taxes, levies, or duties imposed by taxing authorities. Where required, we will collect those taxes on behalf of the taxing authority and remit those taxes to taxing authorities, <strong>including compliance with UK VAT regulations for digital services.</strong> Otherwise, you are responsible for payment of all taxes, levies, or duties.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We process refunds according to our{' '}
                                    <Link href="/terms/refund" sx={{ color: 'primary.500' }}>
                                        Fair Refund policy
                                    </Link>. <strong>For UK users, you may also have statutory rights to a refund if the Services are faulty or not as described.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Cancellation and Termination
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You are solely responsible for properly canceling your account. Within each of our Services, we provide a simple no-questions-asked cancellation link. <strong>For UK users, you have a 14-day cooling-off period from the start of a paid subscription to cancel and receive a full refund, as required by UK law.</strong> You can find instructions for how to cancel your account in our{' '}
                                    <Link href="/terms/cancellation" sx={{ color: 'primary.500' }}>
                                        Cancellation policy
                                    </Link>. An email or phone request to cancel your account is not automatically considered cancellation. If you need help,{' '}
                                    <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                                        contact our Support team
                                    </Link>.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    All of your content will be inaccessible from the Services immediately upon account cancellation. Within 30 days, all content will be permanently deleted from active systems and logs. Within 60 days, all content will be permanently deleted from our backups. We cannot recover this information once it has been permanently deleted. See export instructions on the settings page of your account (Settings &gt; Account &gt; Export Data).
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    If you cancel the Service before the end of your current paid up month, your cancellation will take effect immediately, and you will not be charged again. We do not automatically prorate unused time in the last billing cycle. See our{' '}
                                    <Link href="/terms/refund" sx={{ color: 'primary.500' }}>
                                        Fair Refund policy
                                    </Link>.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We have the right to suspend or terminate your account and refuse any current or future use of our Services <strong>for reasonable cause, such as violation of these Terms or applicable law, and we will provide notice where feasible except in urgent cases.</strong> Suspension means you and any other users on your account will not be able to access the account or any content in the account. Termination will result in the deletion of your account and all content in your account. <strong>We will exercise this right fairly and in accordance with UK law.</strong> For more details, see our{' '}
                                    <Link href="/terms/use_restrictions" sx={{ color: 'primary.500' }}>
                                        Use Restrictions policy
                                    </Link>.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Verbal, physical, written or other abuse (including threats of abuse or retribution) of a Hubitable employee or officer will result in immediate account termination, <strong>with clear evidence of such abuse required.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Modifications to the Service and Prices
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We promise to support our Services until the end of the Internet. We reserve the right to modify or discontinue any part of our Services, <strong>but we will provide reasonable notice for significant changes affecting your use and, for UK users, an option to terminate without penalty if the change is materially detrimental.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We may change the pricing structure for our products. If we change prices for existing customers, we will give at least 30 days' notice via email and on our websites or Services. <strong>For UK users, significant price increases may allow you to terminate the contract without penalty.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Uptime, Security, and Privacy
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Your use of the Services is at your sole risk, and we provide these Services on an "as is" and "as available" basis, <strong>but we commit to delivering them with reasonable care and skill as required by UK law.</strong> Visit{' '}
                                    <Link href="https://status.hubitable.com" sx={{ color: 'primary.500' }}>
                                        Hubitable Services Status
                                    </Link> for service status.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We take measures to secure your data through backups, redundancies, and encryption.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You agree that Hubitable may process your data as per our{' '}
                                    <Link href="/privacy" sx={{ color: 'primary.500' }}>
                                        Privacy Policy
                                    </Link>. We access your data only:
                                </Typography>
                                <List size="sm" sx={{ mt: 2 }}>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                To help with support requests (with consent).
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                To fix rare errors requiring minimal data access.
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                To safeguard hubitable (via logs/metadata).
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                As required by law (U.S.-based, with non-U.S. requests refused unless via treaty).
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                </List>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    For UK users, we comply with the UK GDPR and Data Protection Act 2018, beyond our CCPA "service provider" status.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    These Terms incorporate the Data Protection Act 2018 UK law, <strong>tailored to UK GDPR requirements.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Copyright and Content Ownership
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    All content posted on the Services must comply with <strong>UK copyright law (e.g., Copyright, Designs and Patents Act 1988)</strong> and U.S. copyright law.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    You give us a limited license to use your content to provide the Services, but you retain all ownership rights.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We may refuse or remove content <strong>reasonably and lawfully</strong> at our discretion.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    The Company owns all intellectual property in the Services. You may not copy or reuse any part without permission. Email{' '}
                                    <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                                        us
                                    </Link> for logo use requests.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Features and Bugs
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        We design our Services with care but make no guarantees they will meet your specific requirements, <strong>though we ensure they are fit for purpose and provided with reasonable care and skill as required by UK law.</strong> Bugs are inevitable, and we prioritize fixes for security and privacy issues.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Services Adaptations and API Terms
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We are not liable for damages from API use or third-party products accessing the API, <strong>except where liability cannot be excluded under UK law.</strong>
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Third parties may not use the API for remote surveillance beyond time tracking, as determined by us.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Abuse or excessive API requests may lead to suspension, <strong>with "abuse" defined in our{' '}
                                    <Link href="/terms/use_restrictions" sx={{ color: 'primary.500' }}>
                                        Use Restrictions policy
                                    </Link>,</strong> after warning unless urgent.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Liability
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        <strong>To the extent permitted by applicable law, the Company shall not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages (including loss of profits, goodwill, use, data, or other intangible losses), resulting from: (i) the use or inability to use the Services; (ii) the cost of substitute goods/services; (iii) unauthorized access to your data; (iv) third-party conduct; or (v) any other matter relating to these Terms or Services.</strong>
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        <strong>Nothing in these Terms excludes or limits our liability for death or personal injury from negligence, fraud, or any liability that cannot be excluded under UK law (e.g., Consumer Rights Act 2015, Unfair Contract Terms Act 1977).</strong>
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        Questions?
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        If you have questions about these Terms, please{' '}
                        <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                            contact our Support team
                        </Link>.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default TermsOfService; 